<template>
  <div
    class="mode-switch"
    :class="{ 'mode-switch--disabled': disabled }"
    role="radiogroup"
    aria-label="Режим игры"
    @keydown="onKeydown"
  >
    <span class="mode-switch__thumb" :style="{ transform: `translateX(${activeIndex * 100}%)` }" aria-hidden="true"></span>
    <button
      v-for="(option, index) in OPTIONS"
      :key="option.value"
      ref="buttons"
      type="button"
      role="radio"
      class="mode-switch__option"
      :class="{ 'mode-switch__option--active': option.value === current }"
      :aria-checked="option.value === current ? 'true' : 'false'"
      :tabindex="option.value === current ? 0 : -1"
      :disabled="disabled"
      @click="select(option.value, index)"
    >
      <AppIcon :name="option.icon" :size="22" />
      <span class="mode-switch__label">{{ option.label }}</span>
    </button>
  </div>
</template>

<script setup>
/**
 * ModeSwitch — переключатель режима игры «Моргание / Открытый рот» (segmented control).
 *
 * <ModeSwitch v-model="mode" />            // mode: 'BLINK' | 'MOUTH'
 * <ModeSwitch :model-value="session.mode" @update:model-value="session.setMode" />
 *
 * props: modelValue ('BLINK'|'MOUTH'), disabled. emits: update:modelValue.
 * Клавиатура: ←/→ переключают. Тактильный отклик в Telegram.
 */
import { computed, ref } from "vue";
import AppIcon from "./AppIcon.vue";
import telegram from "@/services/telegram";

const OPTIONS = [
  { value: "BLINK", label: "Моргание", icon: "eye" },
  { value: "MOUTH", label: "Открытый рот", icon: "mouth" },
];

const props = defineProps({
  modelValue: { type: String, default: "BLINK" },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue"]);
const buttons = ref([]);

const current = computed(() => (props.modelValue === "MOUTH" ? "MOUTH" : "BLINK"));
const activeIndex = computed(() => OPTIONS.findIndex((o) => o.value === current.value));

function select(value, index) {
  if (props.disabled || value === current.value) return;
  telegram.haptic("selection");
  emit("update:modelValue", value);
  const el = buttons.value[index];
  if (el && document.activeElement && document.activeElement.closest(".mode-switch")) el.focus();
}

function onKeydown(event) {
  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
  event.preventDefault();
  const next = event.key === "ArrowRight" ? 1 : 0;
  select(OPTIONS[next].value, next);
}
</script>

<style scoped>
.mode-switch {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 4px;
  border-radius: var(--cb-radius-md);
  background: var(--cb-surface);
  border: 1px solid var(--cb-border);
  box-shadow: var(--cb-inner-highlight), inset 0 2px 8px rgba(0, 0, 0, 0.35);
}

.mode-switch__thumb {
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 4px;
  width: calc(50% - 4px);
  border-radius: calc(var(--cb-radius-md) - 4px);
  background: var(--cb-lime-soft);
  border: 1px solid var(--cb-lime);
  box-shadow: var(--cb-glow-sm), inset 0 0 14px rgba(221, 245, 90, 0.1);
  transition: transform var(--cb-dur) var(--cb-ease);
}

.mode-switch__option {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 0;
  height: 48px;
  padding: 0 10px;
  border: 0;
  background: transparent;
  color: var(--cb-text-2);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: color var(--cb-dur) var(--cb-ease);
}

.mode-switch__option--active {
  color: var(--cb-lime);
}

.mode-switch__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mode-switch--disabled {
  opacity: 0.55;
}

.mode-switch--disabled .mode-switch__option {
  cursor: default;
}

@media (max-width: 360px) {
  .mode-switch__option {
    font-size: 14px;
    gap: 6px;
  }
}
</style>
